'use client'

import { useTransition } from 'react'
import { Check, ChevronUp } from 'lucide-react'
import { useLocale } from 'next-intl'
import { useRouter } from 'next/navigation'

import { LocaleFlag } from '@/components/shared/i18n/LocaleFlag'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { type AppLocale, getLocaleLabel, LOCALE_OPTIONS, writeLocaleCookie } from '@/lib/i18n/locales'
import { cn } from '@/lib/utils'

/** Footer-style language picker: flag + native name, opens upward. */
export function LanguageSwitcher({ className }: { className?: string }) {
  const locale = useLocale() as AppLocale
  const router = useRouter()
  const [isPending, startTransition] = useTransition()

  const select = (next: AppLocale) => {
    if (next === locale) return
    writeLocaleCookie(next)
    startTransition(() => router.refresh())
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'group inline-flex items-center gap-2 rounded-lg px-1.5 py-1 text-sm text-white/80 outline-none transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-brand-accent',
          isPending && 'opacity-60',
          className,
        )}
        aria-label={`Language: ${getLocaleLabel(locale)}`}
      >
        <LocaleFlag locale={locale} />
        {getLocaleLabel(locale)}
        <ChevronUp className="size-3.5 transition-transform group-data-[state=closed]:rotate-180" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-56 rounded-2xl border-white/10 bg-ink-raised/95 p-2 text-white shadow-xl backdrop-blur-md"
      >
        {LOCALE_OPTIONS.map((option) => {
          const active = option.code === locale
          return (
            <DropdownMenuItem
              key={option.code}
              lang={option.code}
              onSelect={() => select(option.code)}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-base text-white focus:bg-white/10 focus:text-white',
                active && 'bg-white/10',
              )}
            >
              <LocaleFlag locale={option.code} className="size-6" />
              <span className="flex-1">{option.label}</span>
              {active && <Check className="size-4" aria-hidden />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
